import {
  Box,
  CircularProgress,
  HStack,
  IconButton,
  Text,
  useDisclosure,
  useToast,
  VStack,
  Switch,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { ReplayRounded } from "@material-ui/icons";
import clsx from "classnames";
import ConfirmationModal from "components/Modals/p2p/Confirmation";
import { isEmpty, toLower, toUpper } from "lodash";
import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";

import toCoinLocale from "util/toCoinLocale";
import {
  useGetP2PProfileQuery,
  useTogglePresenceMutation,
} from "services/p2pApi";
import { lessThanSomeMinutesAgo, toQueryString } from "util/utils";

const getCurrency = (country) => {
  const map = {
    nigeria: "ngn",
    ghana: "ghs",
    kenya: "kes",
  };

  return map[toLower(country)];
};

const adsTypeMap = (type) => {
  const map = {
    buy: "selling",
    sell: "buying",
  };

  return map[type];
};

const ListingFilter = ({
  type,
  page,
  onApply,
  limit,
  resetPage,
  isLoading,
  refetch,
  // params,
  country,
}) => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const {
    data: p2pProfile,
    isLoading: isLoadingP2PProfile,
    isFetching: isFetchingP2PProfile,
    // isUninitialized,
    refetch: refetchP2PProfile,
  } = useGetP2PProfileQuery();
  const [
    togglePresence,
    {
      isLoading: isTogglingPresence,
      isSuccess: isTogglingPresenceSuccess,
      isError: isTogglingPresError,
      error: togglingPresError,
    },
  ] = useTogglePresenceMutation();

  const {
    isOpen: isPresencePreviewOpen,
    onOpen: onPresencePreviewOpen,
    onClose: onPresencePreviewClose,
  } = useDisclosure();
  const lastToggled = useRef();
  const toast = useToast();

  const disabledPresenceToggle = useMemo(() => {
    if (isTogglingPresence) return true;
    if (isLoadingP2PProfile) return true;
    if (isFetchingP2PProfile) return true;
    if (lastToggled.current) {
      const waitSomeMins = 2;
      if (lessThanSomeMinutesAgo(lastToggled.current, waitSomeMins))
        return true;
    }
    if (p2pProfile?.profile?.presence?.updatedAt) {
      const waitSomeMins = 2;
      if (
        lessThanSomeMinutesAgo(
          p2pProfile?.profile?.presence?.updatedAt,
          waitSomeMins
        )
      )
        return true;
    }
  }, [
    isTogglingPresence,
    isLoadingP2PProfile,
    isFetchingP2PProfile,
    p2pProfile?.profile?.presence?.updatedAt,
  ]);

  const toastPresence = useCallback(
    (success, msg = "Something went wrong") => {
      toast({
        position: "bottom-right",
        title: success ? "Success" : "Oops",
        description: msg,
        status: success ? "success" : "error",
        duration: 5000,
        isClosable: true,
      });
    },
    [toast]
  );

  const presenceState = useMemo(() => {
    return {
      disabled: disabledPresenceToggle,
      hide: isLoadingP2PProfile || !p2pProfile?.profile,
      presence: p2pProfile?.profile?.presence?.status || "online",
      goOffline: onPresencePreviewOpen,
      goOnline: async () => {
        try {
          const resp = await togglePresence("online");
          if (resp?.data) {
            lastToggled.current = new Date().toISOString();
            await refetchP2PProfile();
            toastPresence(true, "Your listings are back online");
          } else {
            toastPresence(false, "Something went wrong. Try again later");
          }
        } catch (err) {
          toastPresence(false, "Something went wrong. Try again later");
          console.log("[@goOnline] ", err, {
            isTogglingPresenceSuccess,
            isTogglingPresError,
            togglingPresError,
          });
        }
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    disabledPresenceToggle,
    isTogglingPresError,
    isTogglingPresenceSuccess,
    togglingPresError,
    isLoadingP2PProfile,
    p2pProfile?.profile,
    p2pProfile?.profile?.presence?.status,
  ]);

  const [selectedCoin, setSelectedCoin] = useState(
    searchParams.has("coin") ? searchParams.get("coin") : "all"
  );
  const [coinAmount, setCoinAmount] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("all");
  const [selectedCurrency, setCurrency] = useState(
    getCurrency(country) || "all"
  );
  const [rangeFilterToken, setRangeFilterToken] = useState(selectedCoin);
  const [listingFilter, setListingFilter] = useState("all");

  const coins = [
    { value: "all", label: "ALL" },
    { value: "btc", label: "BTC" },
    { value: "eth", label: "ETH" },
    { value: "usdt", label: "USDT" },
    { value: "celo", label: "CELO" },
    { value: "cusd", label: "cUSD" },
  ];

  const listingPathMap = (filter) => {
    const map = {
      all: "",
      own: "/own",
      others: "/others",
    };

    return map[filter];
  };

  const coinBtnClassname = (selectedCoin, coin) =>
    clsx("p2p2-coin", selectedCoin === coin && "p2-coin-active");

  const handleAmountChange = (e) => {
    setCoinAmount(e?.target?.value);
  };

  const handleCurrencyChange = (e) => {
    setCurrency(e?.target?.value);
  };

  const handlePaymentMethodChange = (e) => {
    setPaymentMethod(e?.target?.value);
  };

  const applyFilter = (filter = {}) => {
    const transformCoin = (_coin) => (_coin === "all" ? "" : _coin);

    const qparams = {
      page,
      limit: limit ?? 10,
      adsStatus: "open",
      adsType: adsTypeMap(type),
      sortBy:
        filter?.coin === "all"
          ? "createdAt:desc"
          : type === "buy"
          ? "price:asc"
          : "price:desc", /// ,tradeOption.unitCost:desc,totalCost:desc
      coins: transformCoin(filter.coin ?? selectedCoin),
    };

    const currency = filter?.currency ?? rangeFilterToken;
    const isCrypto = coins.map((c) => c.value).includes(currency);

    !isCrypto && !isEmpty(coinAmount) && (qparams.currency = currency);

    console.log("Q-PARAMS", qparams);

    const qs = toQueryString(qparams);
    const searchQuery = new URLSearchParams(qs);
    if (!isEmpty(paymentMethod) && paymentMethod !== "all")
      searchQuery.set("paymentOption", paymentMethod);
    if (isCrypto && !isEmpty(coinAmount)) {
      searchQuery.delete("currency");
      searchQuery.delete("fiatRanges");
      searchQuery.set("range", `${coinAmount},40`);
    }
    if (!isCrypto && currency && currency !== "all" && !isEmpty(coinAmount)) {
      searchQuery.set("currency", `${rangeFilterToken}`);
      searchQuery.set("fiatRanges", `${coinAmount},40`);
    }

    if (selectedCurrency && selectedCurrency !== "all") {
      searchQuery.set("currency", selectedCurrency);
    }

    const endpoint = listingPathMap(filter?.listingFilter ?? listingFilter);

    const path = `${endpoint}/?${searchQuery.toString()}`;

    // console.log("SEARCH QUERY", path);
    onApply && onApply(path, { endpoint, qs: searchQuery.toString() });
  };

  const handleCoinChange = (value) => {
    setSelectedCoin(value);
    resetPage && resetPage();
    searchParams.set("coin", value);
    history.replace(`${location.pathname}?${searchParams.toString()}`);
    applyFilter({ coin: value });
  };

  const handleListingFilterChange = (e) => {
    setListingFilter(e?.target?.value);
    resetPage && resetPage();
    applyFilter({ listingFilter: e?.target?.value });
  };

  const handleRangeFilterTokenChange = (e) => {
    setRangeFilterToken(e?.target?.value);

    // console.log("RANGE FILTER TOKEN", e.target?.value);
    // applyFilter({ currency: e.target?.value });
  };

  const handleTogglePresence = () => {
    if (presenceState?.presence === "online" && presenceState?.goOffline)
      presenceState.goOffline();
    if (presenceState?.presence === "offline" && presenceState?.goOnline)
      presenceState.goOnline();
  };

  const statusComp = useMemo(
    () => ({
      online: (
        <PresenceToggle
          state="online"
          disabled={presenceState?.disabled}
          {...(presenceState?.goOffline && {
            onChange: presenceState?.goOffline,
          })}
        />
      ),
      offline: (
        <PresenceToggle
          state="offline"
          disabled={presenceState?.disabled}
          {...(presenceState?.goOnline && {
            onChange: presenceState?.goOnline,
          })}
        />
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [
      // presenceState?.presence,
      presenceState?.disabled,
      presenceState?.goOffline,
      presenceState?.goOnline,
    ]
  );

  const presenceStyles = useMemo(() => {
    const map = {
      online: {
        color: "green.400",
        label: "Online",
        isChecked: true,
      },
      offline: {
        color: "red.400",
        label: "Offline",
        isChecked: false,
      },
    };

    return map[presenceState?.presence ?? "online"];
  }, [presenceState?.presence]);

  //eslint-disable-next-line
  const onlineStatus = (status) => {
    if (!status) return null;
    return statusComp[status] || null;
  };

  return (
    <Fragment>
      <ConfirmationModal
        isOpen={isPresencePreviewOpen}
        onClose={onPresencePreviewClose}
        isLoading={isTogglingPresence}
        onConfirm={async () => {
          try {
            const resp = await togglePresence("offline");
            if (resp?.data) {
              lastToggled.current = new Date().toISOString();
              await refetchP2PProfile();
              toastPresence(
                true,
                "Some or all of your listings have been taken offline"
              );
              onPresencePreviewClose();
            } else {
              toastPresence(false, "Something went wrong. Try again later");
              onPresencePreviewClose();
            }
          } catch (err) {
            toastPresence(false, "Something went wrong. Try again later");
            onPresencePreviewClose();
            console.log("[@goOffline] ", err, {
              isTogglingPresenceSuccess,
              isTogglingPresError,
              togglingPresError,
            });
          }
        }}
        title="Confirm"
        buttonText={["No", "Go, Offline"]}
      >
        <VStack>
          <Text textAlign="center">
            Some or all of your listings would be taken off the market
            immediately until you toggle back online. Are you sure you want to
            go offline?
          </Text>
        </VStack>
      </ConfirmationModal>
      <Box className="p2p2-box2" p={["1em 0.3em", "1em 2em"]}>
        <HStack
          className="p2p2-box2-top"
          justifyContent="space-between"
          pb=".5em"
          flexDir={[
            "column-reverse",
            "column-reverse",
            "column-reverse",
            "row",
          ]}
          alignItems={["flex-end", "flex-end", "flex-end", "center"]}
        >
          <Box className="p2p2-coins" m="0" flexWrap={["wrap", "no-wrap"]}>
            {coins.map(({ value, label }) => (
              <button
                key={`${value}-coin-filter-button`}
                className={coinBtnClassname(selectedCoin, value)}
                onClick={() => handleCoinChange(value)}
              >
                {toCoinLocale(value)}
              </button>
            ))}
          </Box>

          <HStack p={["12px", "12px", "12px", "0"]}>
            {/* {presenceState &&
              !presenceState?.hide &&
              onlineStatus(presenceState.presence)} */}
            {presenceState && !presenceState?.hide && (
              <FormControl
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Switch
                  id="online-presence"
                  colorScheme="green"
                  disabled={presenceState?.disabled}
                  isChecked={presenceStyles?.isChecked}
                  onChange={handleTogglePresence}
                />
                <FormLabel
                  fontSize="sm"
                  htmlFor="online-presence"
                  mb="4px"
                  ml="6px"
                  color={presenceStyles?.color}
                >
                  {presenceStyles?.label}
                </FormLabel>
              </FormControl>
            )}

            {isLoading && (
              <CircularProgress isIndeterminate color="green" size="18px" />
            )}
            <IconButton
              onClick={refetch}
              _focus={{ shadow: "0 0 0 3px var(--focusColor)" }}
              size="xs"
              aria-label="refetch"
              icon={<ReplayRounded style={{ fontSize: "14px" }} />}
            />
          </HStack>
        </HStack>
        <div className="p2p2-box2-bottom">
          <div className="p2-box2-bottom-left">
            <div>
              <p>Amount</p>
              <Box
                className="p2p2-amount-box"
                id="outline-in"
                sx={{
                  input: {
                    color: "black",
                  },
                }}
              >
                <input
                  //onFocusCapture={setFocus} onBlur={removeFocus}
                  name="searchAmount"
                  onChange={handleAmountChange}
                  value={coinAmount ?? ""}
                  placeholder="Enter amount"
                  type="text"
                />
                {/* <p>{toUpper(selectedCoin)}</p> */}
                <Box
                  as="select"
                  value={rangeFilterToken}
                  onChange={handleRangeFilterTokenChange}
                >
                  <option value={toLower(selectedCoin)}>
                    {toUpper(selectedCoin)}
                  </option>
                  {selectedCurrency || getCurrency(country) ? (
                    <option value={getCurrency(country)}>
                      {toUpper(selectedCurrency || getCurrency(country))}
                    </option>
                  ) : null}
                </Box>
              </Box>
            </div>
            <div>
              <p>Currency</p>
              <select
                className="p2p2-payment-box"
                name="currencyOption"
                onChange={handleCurrencyChange}
                value={selectedCurrency ?? "all"}
              >
                <option value="all">All currencies</option>
                <option value="ngn">NGN</option>
                <option value="ghs">GHS</option>
                <option value="kes">KES</option>
                {/* <option value="changera">Changera</option> */}
              </select>
            </div>
            <div>
              <p>Payment</p>
              <select
                className="p2p2-payment-box"
                name="paymentOption"
                onChange={handlePaymentMethodChange}
                value={paymentMethod ?? "all"}
              >
                <option value="all">All payments</option>
                <option value="bank">Bank transfer</option>
                <option value="bitmama">Bitmama</option>
                {/* <option value="changera">Changera</option> */}
              </select>
            </div>
            <button onClick={applyFilter} className="p2p2-btn2 m-top5">
              Apply
            </button>
          </div>
          <div className="p2-box2-bottom-right">
            <select
              className="p2p2-listings-box"
              onChange={handleListingFilterChange}
              name="listingOption"
              value={listingFilter}
            >
              <option value="all">All listings</option>
              <option value="others">Other listings</option>
              <option value="own">My listings</option>
              {/* <option>Trade history</option> */}
            </select>
            {/* <Button variant="link" onClick={} className="bold green" href="/dashboard/p2pv2/trades">
                {" "}
                Trade history{" "}
              </Button> */}

            <Link
              style={{ marginLeft: "20px" }}
              className="bold green"
              to="/dashboard/p2p/trades"
            >
              Trade history
            </Link>
          </div>
        </div>
      </Box>
    </Fragment>
  );
};

export default ListingFilter;

function PresenceToggle({ state, onChange, disabled }) {
  const toast = useToast();
  const handleChange = (e) => {
    if (disabled) {
      e?.preventDefault();
      e?.stopPropagation();
      toast({
        position: "bottom-right",
        title: "Too many updates",
        description: "Try again in a few minutes",
        status: "warning",
        duration: 9000,
        isClosable: true,
      });
      return;
    } else if (onChange) {
      e?.preventDefault();
      e?.stopPropagation();

      return onChange();
    }
  };

  return (
    <div className="toggle-switch">
      <div className="flex-switch">
        {/* <span className="switch-title">You are {state}</span> */}
        <input
          type="radio"
          name="rdo"
          id="online"
          checked={state === "online"}
          onChange={handleChange}
        />
        <input
          type="radio"
          name="rdo"
          id="offline"
          checked={state === "offline"}
          onChange={handleChange}
        />
        <div className="switch">
          <label htmlFor="online">Online</label>
          <label htmlFor="offline">Offline</label>
          <span></span>
        </div>
      </div>

      <style jsx>
        {`
          .flex-switch {
            display: flex;
          }
          .toggle-switch {
            position: relative;
            width: 250px;
            height: 45px;
          }
          .switch-title {
            top: 4px;
            position: absolute;
            font-size: 0.75em;
            left: 50%;
            width: 150px;
            text-align: center;
            margin: -15px 0 0 -80px;
            transition: all 0.2s ease;
          }
          .switch {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 150px;
            height: 30px;
            text-align: center;
            margin: -12px 0 0 -80px;
            background: #03a438;
            transition: all 0.2s ease;
            border-radius: 25px;
          }
          .switch span {
            position: absolute;
            width: 20px;
            height: 4px;
            top: 50%;
            left: 50%;
            margin: -2px 0px 0px -4px;
            background: #fff;
            display: block;
            transform: rotate(-45deg);
            transition: all 0.2s ease;
          }
          .switch span:after {
            content: "";
            display: block;
            position: absolute;
            width: 4px;
            height: 12px;
            margin-top: -8px;
            background: #fff;
            transition: all 0.2s ease;
          }
          input[type="radio"] {
            display: none;
          }
          .switch label {
            cursor: pointer;
            color: rgba(0, 0, 0, 0.2);
            width: 60px;
            line-height: 30px;
            font-size: 0.85em;
            transition: all 0.2s ease;
          }
          label[for="online"] {
            position: absolute;
            left: 0px;
            height: 20px;
          }
          label[for="offline"] {
            position: absolute;
            right: 0px;
          }
          #offline:checked ~ .switch {
            background: #eb4f37;
          }
          #offline:checked ~ .switch span {
            background: #fff;
            margin-left: -8px;
          }
          #offline:checked ~ .switch span:after {
            background: #fff;
            height: 20px;
            margin-top: -8px;
            margin-left: 8px;
          }
          #online:checked ~ .switch label[for="online"] {
            color: #fff;
          }
          // #online:checked ~ .switch label[for="online"]::after {
          //   content: "";
          //   position: absolute;
          //   width: 10px;
          //   height: 10px;
          //   background: white;
          //   border-radius: 100%;
          //   right: 50%;
          //   top: -5px;
          //   transform: translateX(50%);
          //   box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
          //   z-index: 10;
          // }
          #offline:checked ~ .switch label[for="offline"] {
            color: #fff;
          }
          // #offline:checked ~ .switch label[for="offline"]::after {
          //   content: "";
          //   position: absolute;
          //   width: 5px;
          //   height: 5px;
          //   background: white;
          //   border-radius: 100%;
          //   left: 50%;
          //   bottom: 2px;
          //   // transform: translateX(50%);
          // }
        `}
      </style>
    </div>
  );
}
